/**
 * IndustryBenchmarkCard — Dashboard card wrapper for IndustryBenchmark
 *
 * Fetches the selected case's financial metrics from the API
 * and feeds them into the existing IndustryBenchmark visualization.
 * Shows loading/empty states when no case is selected.
 */

import React, { useState, useEffect } from 'react';
import { BarChart3, RefreshCw } from 'lucide-react';
import { api } from '../../utils/api';
import IndustryBenchmark from '../IndustryBenchmark';

interface IndustryBenchmarkCardProps {
  reportId?: string;
  className?: string;
}

interface FinancialMetrics {
  businessType?: string;
  naicsCode?: string;
  cogsRatio: number;
  grossMargin: number;
  operatingExpenseRatio: number;
  netProfitMargin: number;
  hasReconstructedData: boolean;
}

export default function IndustryBenchmarkCard({ reportId, className = '' }: IndustryBenchmarkCardProps) {
  const [metrics, setMetrics] = useState<FinancialMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!reportId) {
      setMetrics(null);
      return;
    }
    fetchMetrics(reportId);
  }, [reportId]);

  const fetchMetrics = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const report = await api.get(`/api/v1/reports/${id}`);

      const result = report.result || {};
      const financialData = report.financial_data || {};
      const businessType = report.business_type || report.data?.business_type || '';

      // Calculate financial ratios from report data
      let totalRevenue = 0;
      let totalCogs = 0;
      let totalExpenses = 0;
      let totalNetProfit = 0;
      let monthCount = 0;

      // Try canonical_data first (granular P&L)
      const canonical = report.data?.canonical_data || report.canonical_data;
      if (canonical?.periods) {
        for (const period of canonical.periods) {
          const rev = period.revenue || 0;
          const cogs = period.cogs || period.cost_of_goods_sold || 0;
          const exp = period.total_expenses || period.expenses || 0;
          const net = period.net_income || period.net_profit || (rev - exp);
          totalRevenue += rev;
          totalCogs += cogs;
          totalExpenses += exp;
          totalNetProfit += net;
          monthCount++;
        }
      }

      // Fallback to financial_data year arrays
      if (monthCount === 0) {
        for (const key of Object.keys(financialData)) {
          if (!key.startsWith('data_') || !Array.isArray(financialData[key])) continue;
          for (const m of financialData[key]) {
            if (!m || typeof m !== 'object') continue;
            const rev = m.revenue || 0;
            const cogs = m.cogs || 0;
            const payroll = typeof m.payroll === 'object' ? (m.payroll.total || 0) : (m.payroll || 0);
            const expenses = Object.values(m.expenses || {}).reduce((a: number, b: any) => a + (Number(b) || 0), 0);
            totalRevenue += rev;
            totalCogs += cogs;
            totalExpenses += cogs + payroll + expenses;
            totalNetProfit += rev - (cogs + payroll + expenses);
            monthCount++;
          }
        }
      }

      // Use result-level data if available
      if (monthCount === 0 && result.calculation_detail) {
        const cd = result.calculation_detail;
        const bpRev = cd.benchmark_period?.revenue || 0;
        const vpMargin = cd.variable_profit_analysis?.variable_margin_pct || 0;
        if (bpRev > 0) {
          totalRevenue = bpRev;
          totalCogs = bpRev * 0.35; // estimate
          totalExpenses = bpRev * (1 - vpMargin / 100);
          totalNetProfit = bpRev * (vpMargin / 100);
          monthCount = 12;
        }
      }

      if (totalRevenue > 0) {
        setMetrics({
          businessType,
          naicsCode: report.naics_code,
          cogsRatio: (totalCogs / totalRevenue) * 100,
          grossMargin: ((totalRevenue - totalCogs) / totalRevenue) * 100,
          operatingExpenseRatio: (totalExpenses / totalRevenue) * 100,
          netProfitMargin: (totalNetProfit / totalRevenue) * 100,
          hasReconstructedData: report.data?.reconstructed === true || report.fromBulkUpload === true,
        });
      } else {
        setMetrics(null);
        setError('No financial data available for benchmark comparison');
      }
    } catch (err: any) {
      console.error('Failed to fetch benchmark metrics:', err);
      setError('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  // No case selected
  if (!reportId) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-6 ${className}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <BarChart3 className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Industry Benchmarks</h3>
        </div>
        <div className="text-center py-8">
          <BarChart3 className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Select a case to view industry benchmark comparison</p>
        </div>
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-6 ${className}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <BarChart3 className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Industry Benchmarks</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-5 h-5 text-blue-500 animate-spin mr-2" />
          <span className="text-sm text-gray-500">Loading benchmark data...</span>
        </div>
      </div>
    );
  }

  // Error / no data
  if (error || !metrics) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Industry Benchmarks</h3>
          </div>
          <button
            onClick={() => reportId && fetchMetrics(reportId)}
            className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded"
            title="Retry"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <div className="text-center py-6">
          <p className="text-sm text-gray-500">{error || 'No benchmark data available'}</p>
        </div>
      </div>
    );
  }

  // Render with data
  return (
    <div className={className}>
      <IndustryBenchmark
        businessType={metrics.businessType}
        naicsCode={metrics.naicsCode}
        cogsRatio={metrics.cogsRatio}
        grossMargin={metrics.grossMargin}
        operatingExpenseRatio={metrics.operatingExpenseRatio}
        netProfitMargin={metrics.netProfitMargin}
        hasReconstructedData={metrics.hasReconstructedData}
      />
    </div>
  );
}
